// SplatRasterizer — Set12 (path-driven material assignment) P2: pure data-in → data-out,
// MaterialRegions → ≤4 material indices + per-slot SIGNED DISTANCES per terrain SAMPLE (plan
// §3.3/§5-P2).  Emits signed distances in METRES (negative = inside the region) instead of hard
// weights: the fragment shader feathers them per fragment in P3; until then `packSplatChunk`
// thresholds the distance at 0 (weight = d < 0 ? 1 : 0) so P2 renders visually identical to the
// Set9 hard weights.  Top-K selection per sample (dominance, weight) + the base fallback in slot
// 0; the per-sample winning-REGION index rides into `u_splatRegion` (region params texture row).
//
// P6 — MASKS: each region's `masks` (elevationBand / slopeBand, §3.5) are evaluated PER SAMPLE
// here (the elevation + slope lattices live on `SplatSampleSource`, not in the shader) and BAKED
// into the stored signed distance via `maskedDistance` — the exact inverse of the P3 smoothstep
// feather, so the shader's `regionWeight` over the baked distance equals the region's falloff ×
// the composite mask factor with NO shader changes.  A masked-out candidate ranks as OUTSIDE
// (`inside = d < 0 && maskF > 0`) — it cannot win the top-K, the winner slot, or the opaque
// clear, and an unmasked lower-dominance region shows instead.  Maskless regions are
// byte-identical to P5 (the bake is the identity for factor ≥ 1).

import {
	SPLAT_MAX_MATERIALS,
	packSplatChunk,
	type SplatChunk,
	type SplatRasterizeOptions,
} from "./SplatTypes.js";
import type {MaterialRegion, RegionDistanceField} from "./MaterialRegions.js";
import {clampDistanceForStorage, maskedDistance, rescaleRegionDistance, sampleRegionDistanceAt, buildRegionDistanceField, regionDistanceOverRect, regionFalloff, regionMaskFactor} from "./MaterialRegions.js";
import {Rect} from "../../../Utils/ThreeJS/VectorStructs.js";

/** Error type for splat rasterisation failures (unknown recipe ids, >4 distinct materials, malformed input). The renderer catches it and falls back to the fallback material — never black tiles. */
export class SplatRasterizeError extends Error {
	constructor(message: string) { super(message); this.name = "SplatRasterizeError"; }
}

/** Distance from P to segment AB (mirror of ConstraintRasterizer's local — kept here to avoid widening that module's exports). */
function distPointToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
	const dx = bx - ax, dy = by - ay;
	const lenSq = dx * dx + dy * dy;
	let t = lenSq > 0 ? ((px - ax) * dx + (py - ay) * dy) / lenSq : 0;
	t = Math.max(0, Math.min(1, t));
	const cx = ax + t * dx, cy = ay + t * dy;
	const ex = px - cx, ey = py - cy;
	return Math.sqrt(ex * ex + ey * ey);
}

/** Slope magnitude (m/m) from a normal (worker's z-up frame: gradient in nx/ny, up in nz). Pure — exported for tests. */
export function slopeFromNormal(nx: number, ny: number, nz: number): number {
	const up = Math.max(Math.abs(nz), 1e-6);
	return Math.hypot(nx, ny) / up;
}

/** Resolve the procedural-material recipe id(s) present at a single world point (x, y), for the
 *  Stats-panel "Materials at mouse" readout.  Mirrors `rasterizeSplatChunk`'s per-sample top-K
 *  ranking (dominance DESC, weight DESC, inside-first; P6 masks as weight multipliers) so the
 *  answer matches what the material system renders at that point.  Returns the recipe ids in
 *  SLOT order: slot 0 = the map's base (fallback) recipe, then the WINNING region's recipe if the
 *  point is inside that region (`d < 0 && maskF > 0`).  An outside/empty sample yields just the
 *  base recipe.  `elevation`/`slope` are the point's terrain elevation (m) + slope (m/m), used to
 *  evaluate each region's masks ({@link regionMaskFactor}). */
export function recipesAtPoint(
	x: number, y: number, elevation: number, slope: number,
	regions: readonly MaterialRegion[] = [],
	baseRecipeID = "dirt",
	maxMaterials: number = SPLAT_MAX_MATERIALS,
): string[] {
	const maxRegionSlots = Math.min(SPLAT_TOP_K_REGIONS, Math.max(1, Math.min(maxMaterials, SPLAT_MAX_MATERIALS)) - 1);
	const best: {ri: number; d: number; w: number; m: number}[] = [];
	for (let ri = 0; ri < regions.length; ri++) {
		const r = regions[ri];
		const mode = r.pathFeature.mode;
		const feather = r.pathFeature.feather ?? 0;
		// `strokeWidth` is the FULL stroke width; the signed-distance shift is the radius (half).
		const strokeW = (r.pathFeature.strokeWidth ?? 0) / 2;
		const clampAmt = feather + (r.pathFeature.edgeNoise?.amplitude ?? 0);
		const cap = clampAmt + strokeW + 1;
		const field = buildRegionDistanceField(mode, r.polylines, r.closed);
		const d = sampleRegionDistanceAt(field, x, y, cap) - strokeW;
		const maskF = regionMaskFactor(r.pathFeature.masks, elevation, slope);
		const w = regionFalloff(d, feather) * maskF;
		const dom = r.dominance;
		const inside = d < 0 && maskF > 0;
		let insertAt = 0;
		while (insertAt < best.length) {
			const b = best[insertAt];
			const bDom = regions[b.ri].dominance;
			const bInside = b.d < 0 && b.m > 0;
			if (inside != bInside) { if (inside) break; else insertAt++; continue; }
			if (dom > bDom || (dom == bDom && w > b.w)) break;
			insertAt++;
		}
		if (insertAt >= maxRegionSlots) continue;
		best.splice(insertAt, 0, {ri, d, w, m: maskF});
		if (best.length > maxRegionSlots) best.pop();
	}
	const ids = [baseRecipeID];
	if (best.length > 0 && best[0].d < 0 && best[0].m > 0) {
		const recipe = regions[best[0].ri].pathFeature.recipeID;
		if (recipe != "" && recipe != baseRecipeID) ids.push(recipe);
	}
	return ids;
}

/** Per-sample inputs the rasterizer consumes (computed by the caller from the chunk's heights/normals arrays). Never mutated. */
export interface SplatSampleSource {
	/** World X of sample (row, col) — the chunk's bottom-left + fractional lattice offset. */
	x(row: number, col: number): number;
	/** World Y of sample (row, col). */
	y(row: number, col: number): number;
	/** Terrain elevation at sample (row, col). */
	elevation(row: number, col: number): number;
	/** Slope magnitude (m/m) at sample (row, col). */
	slope(row: number, col: number): number;
}

/** The P2 rasteriser's per-chunk region context: the regions overlapping this chunk (bbox broad
 *  phase, plan §3.3 step 1) + the chunk's resolved fallback recipe id + the top-K cap. */
export interface SplatRegionContext {
	/** Regions whose (feather+noiseAmp)-inflated bbox intersects the chunk's sample bounds. */
	regions: MaterialRegion[];
	/** Parallel to `regions`: each region's index in the FULL map region list (the params-texture
	 *  row the region payload must reference — the renderer builds one row per map region). */
	originalIndices: number[];
	/** The chunk's base recipe (slot 0). */
	baseRecipeID: string;
	/** Max non-base material slots (≤ SPLAT_MAX_MATERIALS - 1). */
	maxRegionSlots: number;
}

/** How many non-base material slots a chunk may carry (plan §3.3: top K = 3 regions + the base
 *  recipe in slot 0; the 4-way lerp + RGBA distance texel are the binding constraints). */
export const SPLAT_TOP_K_REGIONS = SPLAT_MAX_MATERIALS - 1;

// ============================================================
// P4 — per-chunk recipe-set helpers (plan §4.3 A + §5-P4)
// ============================================================

/** The P4 chunk recipe set: the chunk's per-chunk material set in SLOT order (slot 0 = the base
 *  fallback).  This is what drives the per-chunk program key — two chunks whose sets differ
 *  render through different programs, and the program cache is keyed by the set hash (D1). */
export interface ChunkRecipeSet {
	recipeIDs: string[];
	/** The regions (in the caller's list order) whose recipes were dropped by the per-chunk cap —
	 *  the over-stack diagnostic's data (plan §4.3 C).  Present on the full rasterise path; empty
	 *  on the cheap bbox-overlap enumerator (it cannot see slot pressure — only the rasteriser can). */
	overflowDropped: string[];
}

/** Assign each of the chunk's regions a material slot by RANKING order (Set9 P3: the regions
 *  are canonically sorted by dominance DESC, recipe-id ASC; slots go 1..maxRegionSlots in that
 *  order, one slot per MAXIMAL CONTIGUOUS SAME-RECIPE RUN — same-recipe regions coalesce only
 *  when nothing interleaves them; regions beyond the cap are dropped, reported via
 *  `overflowDropped`).  Slot 0 is reserved for the base recipe.  Returns the parallel
 *  `slotsByRegion` array (region index → slot; -1 = dropped) + the slot-ordered recipe id list
 *  (a SEQUENCE that can repeat a recipe id for an interleaved run).
 *
 *  P4 — EXTRACTED from {@link rasterizeSplatChunk} so the program-key builder can reuse it: the
 *  rasteriser needs the slot map to write distances, and the renderer needs the SAME slot
 *  assignment (not a re-derivation) to know which recipe set a chunk rendered with.  Also the
 *  single source of truth for the per-chunk top-K + `overflowDropped` (plan §4.3 A + C).
 *  @throws SplatRasterizeError on an empty/unknown region recipe id (a data error — never render black).
 *
 *  **2026-08-12 — CANONICAL SLOT ORDER (the GrassInsideSnow snow→water bug fix).**  Slot
 *  assignment previously walked the region list in bridge order (dominance DESC, REGION-id
 *  ASC).  Two regions with the SAME dominance but DIFFERENT recipes were therefore ordered by
 *  their region-id — which is chunk-DEPENDENT (a coarse chunk overlapping both regions orders
 *  them differently than a fine chunk overlapping only one).  The SAME recipe SET thus got
 *  DIFFERENT slot orders for different chunk sizes.  Because the per-chunk program lookup
 *  ({@link TerrainMaterialManager.getProgramForSet}) keys the cache by the ORDER-SENSITIVE
 *  `recipeIDs.join(",")`, a chunk whose slot order differed from the warmed/enumerated one
 *  MISSED the cache and fell back to the base-less fast-path program → every slot read the
 *  wrong recipe → e.g. snow rendered as water.
 *
 *  Fix: slot order is now a CANONICAL, chunk-independent function of the recipe SET — recipes
 *  are ranked by (dominance DESC, RECIPE-id ASC) and slots assigned in that order.
 *  The slot order of a set no longer depends on which regions happen to overlap a given chunk,
 *  so the program-key (id order) always matches the rasterised splat (slot order).  The base
 *  recipe keeps slot 0; the shader's equal-dominance smin assumption ("equal-dominance regions
 *  are consecutive slots") still holds — the canonical sort groups equal dominance together.
 *
 *  **2026-08-30 — Set9 P3: DOMINANCE-RUN SPLITTING (Part F).**  The above "one slot per DISTINCT
 *  recipe" is too coarse: two same-recipe regions collapse onto one slot even when ANOTHER
 *  recipe's dominance sits BETWEEN theirs — a single slot (one place in the blend chain) cannot
 *  express both paint positions, producing a discontinuity at the boundary between the two
 *  members' areas of influence.  The user's refinement (plan §4.1): sort the chunk's regions by
 *  (dominance DESC, recipe-id ASC), then coalesce only MAXIMAL CONTIGUOUS RUNS of the same
 *  recipe — one slot per run.  Two same-recipe regions share a slot iff no region of a DIFFERENT
 *  recipe has a dominance strictly between theirs ("relative dominance within the chunk", not
 *  absolute).  Equal-dominance same-recipe regions are always adjacent after the sort (recipe-id
 *  tiebreak) so they always coalesce — F only ever splits on GENUINE interleaving.
 *
 *  Consequence: the SAME recipe id can occupy TWO slots, so `recipeIDs` becomes a SEQUENCE with
 *  possible duplicates (e.g. `[starfield, GJ, grass, GJ]`) and the recipe→slot map
 *  (`slotOfRecipe`) is GONE — `slotsByRegion` (region → slot) is the authoritative output, one
 *  entry per REGION.  `ResolveRecipeSet` maps positionally and tolerates duplicates; the
 *  manager's variant rows + set-hash memo keys are order-sensitive + duplicate-safe; the
 *  per-slot aggregates (dominance/softUnion/noise) are per-RUN.  Feather NEVER splits a slot
 *  (the §3.2 sign-saturation bake carries a feather-0 member onto a soft slot exactly), so the
 *  run predicate is exactly the recipe id — nothing else. */
export function assignRegionSlots(
	regions: readonly MaterialRegion[],
	opts: {knownRecipeIDs: Set<string>; baseRecipeID: string; maxMaterials: number},
): {slotsByRegion: number[]; recipeIDs: string[]; usedSlots: number; overflowDropped: string[]; slotDominance: number[]; slotSoftUnion: number[]; slotNoiseAmp: number[]; slotNoiseScale: number[]} {
	const maxMaterials = Math.max(1, Math.min(opts.maxMaterials, SPLAT_MAX_MATERIALS));
	const maxRegionSlots = Math.min(SPLAT_TOP_K_REGIONS, maxMaterials - 1);

	// A region referencing an unknown recipe is a DATA error — fail loudly at rasterise time
	// (never render black; the renderer catches SplatRasterizeError and falls back to base).
	for (const r of regions) {
		const id = r.pathFeature.recipeID;
		if (id == "") throw new SplatRasterizeError(`region "${r.id}" has an empty recipeID`);
		if (!opts.knownRecipeIDs.has(id)) throw new SplatRasterizeError(`region "${r.id}" references unknown recipe "${id}" (known: ${[...opts.knownRecipeIDs].join(", ")})`);
	}

	// CANONICAL order (2026-08-12 + Set9 P3): sort the regions by (dominance DESC, recipe-id
	// ASC) — a pure function of the (dominance, recipe) pair, independent of which regions
	// overlap a given chunk or their bridge/list order.  The old canonical-slot fix ranked
	// DISTINCT RECIPES this way; Set9 P3 (Part F) sorts the REGIONS themselves, so the run
	// predicate (consecutive same-recipe spans) walks this canonical order.  Equal-dominance
	// same-recipe regions always sort adjacent (the recipe-id tiebreak groups them), so they
	// coalesce onto one slot; a DIFFERENT recipe whose dominance sits between two same-recipe
	// regions splits them into separate runs/slots (plan §4.1).
	//
	// NOTE — the caller's `regions` array is NOT mutated: we sort an INDEX list and map the
	// resulting slots back to the ORIGINAL region indices (`slotsByRegion[origRi] = slot`),
	// which `rasterizeSplatChunk` indexes by the ORIGINAL list position.  The `sortOrder` array
	// holds the original indices in canonical (sorted) order — the enumerator/renderer paths
	// pass the same region list as the rasteriser, so the sorted order is identical across all
	// three call sites (the consistency constraint that keeps the program key and the splat
	// slot order in agreement).
	const sortOrder = regions.map((_, i)=>i).sort((a, b)=> {
		const ra = regions[a], rb = regions[b];
		const byDom = rb.dominance - ra.dominance;
		if (byDom != 0) return byDom;
		const raID = ra.pathFeature.recipeID, rbID = rb.pathFeature.recipeID;
		return raID < rbID ? -1 : raID > rbID ? 1 : 0;
	});

	// Assign slots by walking the canonical (sorted) order, coalescing MAXIMAL CONTIGUOUS
	// SAME-RECIPE RUNS — one slot per run (Set9 P3 / plan §4.1).  `slotsByRegion` (region →
	// slot; -1 = dropped) is the AUTHORITATIVE output — the recipe→slot map is deleted (the
	// same recipe may occupy two slots when a run is interleaved).  Feather NEVER splits a run
	// (plan §4.3 — the §3.2 sign-saturation bake carries a feather-0 member onto a soft slot).
	const slotsByRegion = new Array<number>(regions.length).fill(-1);
	// slot 0 is reserved for the base recipe (never a region's recipe normally, but be safe).
	const recipeIDs: string[] = [opts.baseRecipeID];
	const slotDominance: number[] = [0];
	let usedSlots = 1;
	const overflowDropped: string[] = [];
	let prevRecipe: string | null = null;
	for (let pos = 0; pos < sortOrder.length; pos++) {
		const ri = sortOrder[pos];
		const recipe = regions[ri].pathFeature.recipeID;
		// A new RUN starts when the recipe differs from the previous region's (a maximal
		// contiguous same-recipe span in the canonical order).
		if (pos > 0 && recipe != prevRecipe) usedSlots++;
		// Beyond the cap → the region's recipe is dropped from this chunk (+ reported).
		if (usedSlots > maxRegionSlots) { overflowDropped.push(regions[ri].id); prevRecipe = recipe; continue; }
		slotsByRegion[ri] = usedSlots;
		// Per-slot effective dominance = the MAX over the regions sharing the slot (the smin
		// gate's payload — a run may hold same-recipe regions with ADJACENT-but-different
		// dominances and nothing between them; they share one slot carrying the group max).
		slotDominance[usedSlots] = Math.max(slotDominance[usedSlots] ?? 0, regions[ri].dominance);
		recipeIDs[usedSlots] = recipe;
		prevRecipe = recipe;
	}
	// NOTE: `recipeIDs` / `slotDominance` carry one entry per USED slot INCLUDING the base
	// (slot 0), so their length == `usedSlots + 1` (BEWARE: `usedSlots` is the last assigned
	// slot INDEX, NOT a count — slot 0 = the base + runs at indices 1..usedSlots).  They are
	// padded to SPLAT_MAX_MATERIALS below / by the caller.  Because the same recipe can occupy
	// two slots (an F-split run), `recipeIDs` is a SEQUENCE with possible duplicates —
	// `ResolveRecipeSet` maps positionally + tolerates duplicates, and the manager's
	// variant/set keys are order-sensitive + duplicate-safe.
	// Regions that could not be placed (per-chunk cap) — the P4 diagnostic
	// (materials-per-chunk exceeded); the data still renders (they're dropped per chunk).
	// (`overflowDropped` was populated inline above.)
	// P5 — pad `slotDominance` to SPLAT_MAX_MATERIALS (the uniform is a vec4; a slot beyond
	// the chunk's used count is never read by the chain — the shader iterates only
	// recipes.length-1 slots — so the padding value is irrelevant, but the uniform must be
	// fully populated for the renderer's upload gate).
	while (slotDominance.length < SPLAT_MAX_MATERIALS) slotDominance.push(0);
	// Set12 followup (2026-08-25) — per-slot SOFT-UNION opt-in: slot i is a soft-union
	// participant iff EVERY region sharing that slot has `softUnion` explicitly set (AND —
	// the "safe by construction" rule).  A slot whose regions don't all opt in is NOT a soft
	// union group, so a far-but-bbox-overlapping equal-dominance material in the adjacent slot
	// can never be accidentally soft-unioned with it (the "water replaces base" footgun).
	// The shader only smin-unions an equal-dominance PAIR when BOTH slots are soft-union
	// participants.  Slot 0 (the base) is never smin'd — keep its entry false.
	const slotSoftUnion = new Array<number>(SPLAT_MAX_MATERIALS).fill(0);
	{
		// Set9 P3 followup (2026-08-30): `usedSlots` is the LAST assigned slot INDEX (= count-1,
		// since slot 0 = the base + runs are assigned indices 1..usedSlots), NOT a slot count.
		// A count-sized `seen`/`allOptedIn` would be one short → the highest slot's opt-in flag
		// lands out of bounds + the `s < usedSlots` finaliser loop SKIPS it, forcing the last
		// run-slot's `slotSoftUnion` to 0 even with every member opted in — equal-dominance pairs
		// involving the top slot fall through to the ordered paint-over instead of the soft smin.
		const slotCount = usedSlots + 1;
		// Start each slot as "no regions opted out yet"; an absent flag counts as opted-out.
		const seen = new Array<boolean>(slotCount).fill(false);
		const allOptedIn = new Array<boolean>(slotCount).fill(true);
		for (let ri = 0; ri < regions.length; ri++) {
			const s = slotsByRegion[ri];
			if (s < 0) continue;
			seen[s] = true;
			// A region opts in iff `softUnion === true` (absent/false = opted out).
			if (regions[ri].pathFeature.softUnion !== true) allOptedIn[s] = false;
		}
		for (let s = 1; s < slotCount; s++) slotSoftUnion[s] = (seen[s] && allOptedIn[s]) ? 1 : 0;
	}
	// Set12 P5 (2026-08-27) — per-slot EDGE-NOISE (the "edge noise gets cut off by the polygon
	// edge" fix).  Each slot's noise amp/scale = the values of the regions sharing that slot,
	// so the shader can displace EACH slot's distance with ITS OWN noise (mirroring the
	// per-slot FEATHER fix above): a fragment just OUTSIDE the polygon (positive distance,
	// blended to the base) still gets the region's warp instead of a straight geometric edge.
	// `slotNoiseAmp[i]` = the MAX amplitude among the regions sharing slot i (0 = no noise);
	// `slotNoiseScale[i]` = the scale of the highest-amplitude region on that slot (1 = the
	// shader's neutral).  Slot 0 (the base) stays 0/1 — never displaced.
	const slotNoiseAmp = new Array<number>(SPLAT_MAX_MATERIALS).fill(0);
	const slotNoiseScale = new Array<number>(SPLAT_MAX_MATERIALS).fill(1);
	for (let ri = 0; ri < regions.length; ri++) {
		const s = slotsByRegion[ri];
		if (s < 0) continue;
		const noise = regions[ri].pathFeature.edgeNoise;
		if (noise && noise.amplitude > 0 && noise.amplitude > slotNoiseAmp[s]) {
			slotNoiseAmp[s] = noise.amplitude;
			slotNoiseScale[s] = noise.scale > 0 ? noise.scale : 1;
		}
	}
	return {slotsByRegion, recipeIDs, usedSlots, overflowDropped, slotDominance, slotSoftUnion, slotNoiseAmp, slotNoiseScale};
}

/** P4 — enumerate the DISTINCT per-chunk recipe sets a region layout produces, from region bbox
 *  ↔ chunk overlap (plan §4.3 A: warm-up can no longer enumerate prefixes of one global list; it
 *  must enumerate the sets that ACTUALLY OCCUR, cheaply, no rasterise).  Two chunks at the same
 *  quadtree LOD are the same world size, so a set is the recipe set of the regions whose
 *  (feather+noiseAmp)-inflated bbox intersects ANY chunk of that size.
 *
 *  The per-chunk slot assignment (and thus the recipe-set key) is a function of the CHUNK's
 *  overlapping region list — the same slot-assignment logic the rasteriser uses
 *  ({@link assignRegionSlots}, which sorts the regions canonically), so the enumerated
 *  sets are EXACTLY the sets the rasteriser produces.  Same-recipe regions in the SAME
 *  dominance run collapse onto one slot (Set9 P3 / plan §4.1 — a maximal contiguous same-recipe
 *  span); a region list beyond the cap is truncated to the top (maxMaterials-1)
 *  ranking regions.  Unlike the full rasterise, the enumerator cannot report per-chunk
 *  `overflowDropped` — it only sees bbox overlap, not per-sample slot pressure — so its sets
 *  carry an empty diagnostic (the rasteriser's `overflowDropped` is authoritative; the P4 test
 *  asserts the enumerator's sets AGREE with the rasteriser's, and the diagnostic separately).
 *
 *  @param chunkSizes The distinct chunk world sizes that occur (the quadtree's LOD levels; e.g.
 *  2048/1024/512/256…).  Each is treated as a square chunk at every position that could overlap
 *  the regions — an enumerator over LOD sizes, not over every individual chunk.
 *  @returns The distinct sets in stable (first-occurrence) order. */
export function enumerateChunkRecipeSets(
	regions: readonly MaterialRegion[],
	chunkSizes: number[],
	opts: {knownRecipeIDs: Set<string>; baseRecipeID: string; maxMaterials: number},
): ChunkRecipeSet[] {
	const sets: ChunkRecipeSet[] = [];
	const seen = new Set<string>();
	// The base-only set ({dirt}) is ALWAYS reachable — a chunk far from every region bbox
	// overlaps nothing and rasterises with just the fallback (plan §3.3: the base fills where
	// no region applies).  Emit it first so the warm-up compiles the 1-way program too (a far
	// chunk must not fall back to the union shader).
	sets.push({recipeIDs: [opts.baseRecipeID], overflowDropped: []});
	seen.add(opts.baseRecipeID);
	for (const size of chunkSizes) {
		// Per LOD size: sweep every chunk (of this size) that could overlap a region bbox.  The
		// union over ALL sizes is needed — a coarse chunk's overlap set is the union of its fine
		// sub-chunks' sets and need not equal ANY single fine chunk's set (two regions each
		// confined to opposite halves of a coarse chunk overlap the coarse chunk but no common
		// fine chunk).  Sets are deduped across sizes (same key = same program).
		for (const r of regions) {
			const inflate = Math.max(r.pathFeature.feather ?? 0, r.pathFeature.edgeNoise?.amplitude ?? 0);
			const bx = r.bbox.x, by = r.bbox.y, bw = r.bbox.width, bh = r.bbox.height;
			const cx = Math.floor(bx / size) * size, cy = Math.floor(by / size) * size;
			// Every chunk whose square intersects the (already inflated) bbox — the same
			// Intersects(strict) test the broad phase uses, but axis-aligned squares here.
			for (let cyy = cy; cyy < by + bh; cyy += size) {
				for (let cxx = cx; cxx < bx + bw; cxx += size) {
					const contributing = filterRegionsContributingToChunk(regions, {x: cxx, y: cyy, width: size, height: size});
					const {recipeIDs} = assignRegionSlots(contributing.regions, opts);
					const key = recipeIDs.join(",");
					if (!seen.has(key)) { seen.add(key); sets.push({recipeIDs, overflowDropped: []}); }
				}
			}
		}
	}
	return sets;
}

/** Rasterise one chunk's splat: ≤4 material indices + per-slot SIGNED DISTANCES per sample (plan
 *  §3.3/§5-P2).  The per-sample candidates are the chunk's bbox-filtered regions (broad phase);
 *  each is ranked by (dominance, provisional falloff weight); the top K = 3 by that ranking fill
 *  slots 1..3 (highest wins slot 1), the base recipe fills slot 0, and every sample emits the
 *  winning region's index (0 = base) into `u_splatRegion`.
 *  @param source Per-sample world coords + elevation + slope (the worker's heights/normals — the exact samples the shader renders).
 *  @returns The packed SplatChunk (distances clamped to ±(feather + noiseAmp)) + usage diagnostics. */
export function rasterizeSplatChunk(
	n: number,
	source: SplatSampleSource,
	opts: SplatRasterizeOptions,
	ctx: SplatRegionContext,
): {chunk: SplatChunk; overflowDropped: string[]; usageCounts: Map<number, number>} {
	const maxMaterials = Math.max(1, Math.min(opts.maxMaterials, SPLAT_MAX_MATERIALS));
	const maxRegionSlots = Math.min(ctx.maxRegionSlots, maxMaterials - 1, SPLAT_TOP_K_REGIONS);
	const regions = ctx.regions;
	// The chunk's region→slot map: `slotsByRegion` (region index → slot; slot 0 = the base
	// recipe), returned by {@link assignRegionSlots}.  Set9 P3 — the recipe→slot map is GONE: a
	// shared recipe (a path split into several meshes) collapses onto one slot ONLY within a
	// maximal dominance run; interleaved runs give the same recipe multiple slots.
	// P4 — the slot assignment (ranking-order top-K + per-chunk cap + overflowDropped) is the
	// EXTRACTED {@link assignRegionSlots} — the same logic the per-chunk program-key enumerator
	// uses, so a chunk's recipeIDs here are exactly the set its program was keyed by.
	const {slotsByRegion, recipeIDs, usedSlots, overflowDropped, slotDominance, slotSoftUnion, slotNoiseAmp, slotNoiseScale} = assignRegionSlots(regions, {
		knownRecipeIDs: new Set(opts.knownRecipeIDs),
		baseRecipeID: ctx.baseRecipeID,
		maxMaterials: Math.min(maxMaterials, maxRegionSlots + 1),
	});
	// Set9 P3 — the recipe→slot map is GONE (`slotOfRecipe` deleted): `slotsByRegion` is the
	// authoritative region→slot output, and the same recipe may occupy two slots (an F-split
	// run).  `usageCounts` is a diagnostic only (never read downstream), so it is keyed by SLOT
	// index rather than recipe id — two slots of the same recipe must not collide.
	const usageCounts = new Map<number, number>([[0, 0]]);
	// Set9 P3 followup (2026-08-30): `usedSlots` is the last assigned slot INDEX (count-1), so
	// the slot count is `usedSlots + 1`.  `s < usedSlots` would skip the top slot here too.
	for (let s = 1; s < usedSlots + 1; s++) {
		usageCounts.set(s, 0);
	}

	const count = n * n;
	const indices = new Uint8Array(count);
	// P3 — absent slots start at +ABSENT_SLOT_DISTANCE (NOT 0): the shader's `regionWeight`
	// over a 0 distance would be 0.5 (a material that is absent would blend at 50% everywhere).
	// A slot that is never written at a sample (its region is not in this sample's top-K, or its
	// recipe is beyond the chunk's used slots) must feather to weight 0 — any stored value ≥ the
	// feather clamp cap does that (regionWeight(x) = 0 for x ≥ +feather).  The per-sample loop
	// overwrites the present slots; the base slot is overwritten per sample too (its stored
	// +huge-clamped value is never consumed — the shader's blend chain starts from the base
	// colour and mixes the region slots over it).
	const ABSENT_SLOT_DISTANCE = 1e4;
	const weights = new Float32Array(count * SPLAT_MAX_MATERIALS).fill(ABSENT_SLOT_DISTANCE);
	// Phase 7 — per-sample winning-REGION index (drives the per-region params texture).
	// 0 = the base (no region won); i = the winning region's index in the FULL map region list
	// PLUS ONE (the params texture keeps a dummy row 0 for the base, so the region rows start at
	// row 1 — row = payload value; 0 never collides with the first map region's index 0).
	const regionsArr = new Uint8Array(count);

	// Assign slots to the chunk's regions in ranking order (plan §3.3: the per-sample top-K
	// selection is by (dominance, weight) — the slot order must match that ranking so the
	// highest-dominance region wins slot 1 across the whole chunk, and a sample's winner is
	// unambiguous.  The region list is already dominance-sorted (DESC) by the bridge).
	// (Slot assignment now happens via {@link assignRegionSlots} above.)

	// Per-sample: evaluate d_r for each chunk-overlapping region, rank by (dominance, weight),
	// select the top K regions by ranking, write their clamped signed distances into their slots.
	// NOTE — the signed-distance representation stores the region-boundary distance WHEREVER the
	// region's material contributes: the winning (top-ranked) region writes its NEGATIVE distance
	// when inside; the runners-up write their POSITIVE (outside) distances too — the P3 shader
	// feathers each slot at its own boundary crossing (`regionWeight` over each slot's distance
	// is what produces the smooth blend; the interpolation needs the positive side).  An outside
	// sample (every candidate positive) falls back to the base (slot 0, d0 = +huge).
	const opaqueSlots = new Set<number>();
	// PERF — the per-sample distance evaluation dominated the whole renderer (profiler:
	// `rasterizeSplatFromArrays → rasterizeSplatChunk → regionDistanceAt` was ~8s of main-thread
	// time after the worker solve).  The distance field is built ONCE per (region, chunk) here
	// (geometry hoisted + WeakMap-cached per polyline in MaterialRegions — a map's regions build
	// it once total across all chunks) and sampled per sample via {@link sampleRegionDistanceAt}
	// (exact bbox gates — a sample that cannot influence the distance returns without touching a
	// segment).  `regionSlots`/`regionDoms` hoist the per-region slot/dominance reads out of the
	// ranking loop.
	const regionFields: RegionDistanceField[] = [];
	const regionSlots = new Int32Array(regions.length);
	const regionDoms = new Float64Array(regions.length);
	const regionStrokeW = new Float64Array(regions.length);
	const regionFeather = new Float64Array(regions.length);
	const regionClampAmt = new Float64Array(regions.length);
	const regionCap = new Float64Array(regions.length);
	const regionMasks: MaterialRegion["pathFeature"]["masks"][] = [];
	// PERF pass 2b — the PER-CHUNK constant-distance short-circuit ({@link regionDistanceOverRect}):
	// NaN = "varies across the chunk, use the per-sample path"; a finite value = the region is
	// cap-saturated across EVERY sample (sign exact), so the per-sample ring walk + ray-cast is
	// skipped entirely.  This is the huge-plains / big-lake case: a viewport deep INSIDE a giant
	// fill is uniformly over-cap but negative, which pass 2's per-sample gates could not collapse.
	const regionConstD = new Float64Array(regions.length).fill(NaN);
	// The chunk's sample rect, from its corner samples (the source's lattice is axis-aligned).
	const s0x = source.x(0, 0), s0y = source.y(0, 0);
	const s1x = source.x(n - 1, n - 1), s1y = source.y(n - 1, n - 1);
	const chunkX = Math.min(s0x, s1x), chunkY = Math.min(s0y, s1y);
	const chunkW = Math.abs(s1x - s0x), chunkH = Math.abs(s1y - s0y);
	// ── Set12 followup (2026-08-29) + Set9 P2 (2026-08-30) — PER-SLOT FEATHER + CLAMP
	//    RESOLUTION + PER-REGION RESCALE (the "same-recipe regions with DIFFERENT feathers
	//    phantom the whole chunk" fix → superseded by the exact per-region rescale).
	//
	// THE GOVERNING RULE (Set9 P2): the shader feathers per SLOT (`u_slotFeather`, one value
	// per slot = the MAX feather among the slot's regions), but `assignRegionSlots` COLLAPSES
	// all regions that share a recipe onto ONE slot.  Set12's fix made the rasteriser bake
	// every stored distance with the SLOT's feather/clamp — that removed the phantom but
	// OVER-CORRECTED: a narrow authored feather was rendered with the slot's wider band (the
	// "actual Grass_Jungle appears in the corner" regression — the Tree area's authored 2 m fade
	// rendered with the slot's 5 m feather, widening its halo 2.5×).
	//
	// Set9 P2 REPLACES that blunt "slot behaves as one region with the softest feather" with an
	// EXACT per-region rescale: each region keeps its OWN authored feather for the mask bake and
	// the ranking falloff, and its stored distance is then RESCALED into the slot's feather
	// scale ({@link rescaleRegionDistance}): `d' = d · slotFeather/regionFeather` for
	// `regionFeather > 0` (a pure change of units — `regionWeight(d', slotFeather) ==
	// regionWeight(d, regionFeather)` exactly), and the sign-saturation bake for
	// `regionFeather == 0`.  The NO-PHANTOM invariant is preserved BY CONSTRUCTION because the
	// saturated value is scaled into the slot's own band: a far region's saturated distance
	// `d ≥ cap` maps to `d' ≥ slotFeather`, so `regionWeight(d', slotFeather) == 0` exactly.
	//
	// Original Set12 note (kept for the record): a slot carrying a SINGLE region resolves to
	// that region's own feather/clamp — a NO-OP for every slot that isn't a same-recipe
	// multi-region union (the overwhelmingly common case), so existing output is byte-identical
	// there (a single region's scale factor is 1).
	const slotFeather = new Array<number>(SPLAT_MAX_MATERIALS).fill(0);
	const slotClampAmt = new Array<number>(SPLAT_MAX_MATERIALS).fill(0);
	for (let ri = 0; ri < regions.length; ri++) {
		const s = slotsByRegion[ri];
		if (s < 0) continue; // dropped by the per-chunk cap — never stored, never feathered
		const pf = regions[ri].pathFeature;
		const feather = pf.feather ?? 0;
		const clampAmt = feather + (pf.edgeNoise?.amplitude ?? 0);
		if (feather > slotFeather[s]) slotFeather[s] = feather;
		if (clampAmt > slotClampAmt[s]) slotClampAmt[s] = clampAmt;
	}
	// Set9 P2 (§3.2) — after resolving the raw per-slot max, RE-DERIVE each slot's clamp so it
	// accommodates the RESCALED distance values: a region rescaled by k = slotF/regF stores
	// `d' = d·k` within ±(slotF + slotF·amp/regF), so the slot clamp must be at least
	// `slotFeather[s] + max_i(amp_i · slotFeather[s]/regF_i)`.  Keeping `slotClampAmt >=
	// slotFeather` preserves the by-construction no-phantom invariant (the rescale alone could
	// have shrunk it below the feather for narrow-region/noise-heavy unions).  The raw max above
	// is the fallback when no region has an edge-noise amplitude (the overwhelmingly common
	// case — clamp = max_i(feather_i) = slotFeather, and the rescale keeps it ≥ that).
	for (let s = 0; s < SPLAT_MAX_MATERIALS; s++) {
		if (slotFeather[s] == 0) continue;
		let scaledClamp = slotFeather[s];
		for (let ri = 0; ri < regions.length; ri++) {
			if (slotsByRegion[ri] != s) continue;
			const pf = regions[ri].pathFeature;
			const regF = pf.feather ?? 0;
			const amp = pf.edgeNoise?.amplitude ?? 0;
			if (amp <= 0) continue;
			const k = regF > 0 ? slotFeather[s] / regF : 0; // feather-0 → the sign-saturation bake saturates at ±slotClampAmt, which we keep ≥ slotFeather
			const member = slotFeather[s] + amp * k;
			if (member > scaledClamp) scaledClamp = member;
		}
		if (scaledClamp > slotClampAmt[s]) slotClampAmt[s] = scaledClamp;
	}
	for (let ri = 0; ri < regions.length; ri++) {
		const r = regions[ri];
		regionFields.push(buildRegionDistanceField(r.pathFeature.mode, r.polylines, r.closed));
		regionSlots[ri] = slotsByRegion[ri];
		regionDoms[ri] = r.dominance;
		// `strokeWidth` is the FULL stroke width; the signed-distance shift is the radius (half).
		const sw = (r.pathFeature.strokeWidth ?? 0) / 2;
		// Set9 P2 — per-region resolved values for the BAKE + RANKING: the region's OWN authored
		// feather (NOT the slot's — the ranking falloff and the P6 mask bake use the region's own
		// authored falloff, the honest measure), and the SLOT's clamp (the storage clamp is a
		// slot property; a region dropped by the per-chunk cap (slot -1) is never stored, so its
		// own values are a harmless fallback).  The actual rescale into the slot's feather scale
		// happens per sample, AFTER the mask bake (see the write path).
		const slot = slotsByRegion[ri];
		const ownFeather = r.pathFeature.feather ?? 0;
		const feather = ownFeather; // region's OWN feather for the bake + ranking
		const clampAmt = slot >= 0 ? slotClampAmt[slot] : ownFeather + (r.pathFeature.edgeNoise?.amplitude ?? 0);
		regionStrokeW[ri] = sw;
		regionFeather[ri] = feather;
		regionClampAmt[ri] = clampAmt;
		// PERF pass 2 — the DISTANCE CAP handed to sampleRegionDistanceAt: beyond this radius the
		// exact distance is unobservable (every consumer saturates), so the sampler may stop the
		// ring search early.  `clampDistanceForStorage` maps |d| ≥ clampAmt to ±clampAmt, and both
		// `regionFalloff` and `maskedDistance` saturate at |d| ≥ feather ≤ clampAmt.  The cap is on
		// the RAW sampled value while the consumed one is `raw - sw` (sw = strokeWidth/2, the
		// radius), so add sw back; +1 m of slack keeps the boundary case (|d| exactly at the
		// clamp) on the exact side.
		regionCap[ri] = clampAmt + sw + 1;
		regionMasks.push(r.pathFeature.masks);
		const constD = regionDistanceOverRect(regionFields[ri], chunkX, chunkY, chunkW, chunkH, regionCap[ri]);
		if (constD != null) regionConstD[ri] = constD;
		if (r.pathFeature.opaque) { const s = slotsByRegion[ri]; if (s >= 0) opaqueSlots.add(s); }
	}
	// Set12 followup (2026-08-25/27/29) — per-slot RESOLVED FEATHER (the "fade-to-wrong-material"
	// fix): each slot's feather = the MAX `feather` among the regions sharing that slot.  The
	// shader feathers the slot with THIS value, so a far-but-bbox-overlapping region's positive
	// distance (stored clamped to ±`slotClampAmt` >= `slotFeather`) saturates to weight 0 under
	// it (no phantom bleed under the fading top region).
	//
	// 2026-08-27 — this is the region's TRUE `feather`, NOT `clampAmt` (feather + amp).  Two
	// things were wrong before:
	//  (1) PHANTOM — `slotFeather = clampAmt` widened the feather band to ±clampAmt, so a far
	//      saturated distance (dd = clampAmt + fbm*amp >= feather, which is still < clampAmt for
	//      feather < clampAmt) landed INSIDE the band → weight > 0 → the phantom.  With the true
	//      feather, dd >= feather lands exactly at the band edge → weight 0 (see regionWeight's
	//      phantom note in ShaderAssembler) — no shader gate needed.
	//  (2) FEATHER-0 SOFTENING — `slotFeather = 0 + amp = amp` made regionWeight's `feather <= 0`
	//      hard-edge branch never fire, so a feather-0 boundary rendered with a soft
	//      smoothstep(-amp, +amp, dd) band instead of a crisp hard edge.
	// Using the true `feather` restores the hard edge for feather-0 regions (crisp teeth) while
	// STILL saturating far distances: `slotClampAmt >= slotFeather`, so
	// `regionWeight(slotClampAmt, slotFeather)` = 0 (the fade-to-wrong-material property holds
	// for any feather >= 0, including 0 via the hard-edge branch).  A `d < noiseAmp` warp gate
	// was TRIED but REINTRODUCED the feather>0 cutoff (it cut the noise mid-feather-band); the
	// gate is removed — the true-feather saturation alone prevents the phantom.  Slot 0 (the
	// base) keeps its entry for slot alignment; the shader never feathers it (its distance is
	// never consumed).  Parallel to `slotDominance`.
	//
	// 2026-08-29 — `slotFeather`/`slotClampAmt` are now resolved ABOVE (before the per-region
	// prep loop) so the per-sample bake and the storage clamp use the SLOT's values, keeping
	// `slotClampAmt >= slotFeather` true even when same-recipe regions authored DIFFERENT
	// feathers.  See the long comment at the resolution site for the full root cause.
	for (let row = 0; row < n; row++) {
		for (let col = 0; col < n; col++) {
			const x = source.x(row, col);
			const y = source.y(row, col);
			// P6 — per-sample MASK inputs: the region masks (elevationBand / slopeBand, §3.5)
			// are weight MULTIPLIERS evaluated per sample from the chunk's elevation + slope
			// lattices (the exact samples the shader renders — `source.elevation`/`source.slope`).
			// The composite factor feeds BOTH the ranking (a masked-out candidate must not win
			// the top-K — "only apply to certain elevations inside", the stretch goal) AND the
			// stored distance (via the P6 BAKE, `maskedDistance` — the shader's regionWeight over
			// the baked distance equals the region's falloff × the factor, so a masked sample's
			// material fades to the under-layer even when the region's distance is deeply
			// negative — the "no hard mask edge" property).
			const elevation = source.elevation(row, col);
			const slope = source.slope(row, col);
			// Candidates: every region whose bbox intersects the CHUNK (the broad phase above
			// already filtered); per-sample we still need the weight ranking, so compute the
			// provisional falloff for each and keep the top K = 3 by (dominance DESC, weight).
			// The dominant slot-holder is written FIRST into the lower slot index (paint-over
			// order at blend time); ties break by dominance then weight then region list order
			// (the bridge's deterministic id order) — plan §4.2 A.
			const best: {ri: number; d: number; w: number; m: number}[] = [];
			for (let ri = 0; ri < regions.length; ri++) {
				const constD = regionConstD[ri];
				const d = (constD == constD ? constD : sampleRegionDistanceAt(regionFields[ri], x, y, regionCap[ri])) - regionStrokeW[ri];
				// P6 — the MASK factor (composite over the region's masks): multiply into the
				// provisional weight so a masked-out sample (factor 0) ranks BELOW every
				// unmasked candidate — the "only apply to certain elevations inside" stretch
				// goal.  The factor ALSO rides into the stored distance (the P6 bake below —
				// `maskedDistance`) so the shader's regionWeight equals falloff × factor with
				// NO shader changes (plan §4.5 "multiply weight", rasteriser-side).
				const maskF = regionMaskFactor(regionMasks[ri], elevation, slope);
				// provisional weight purely to RANK candidates (plan §3.3 step 2)
				const w = regionFalloff(d, regionFeather[ri]) * maskF;
				// Keep the top maxRegionSlots by (dominance DESC, weight DESC) — a linear scan
				// over the region list (a handful after the broad phase) is cheaper than sorting.
				// The best-list is DESCENDING-rank (highest first): the new candidate goes BEFORE
				// the first existing candidate it OUTRANKS.  (The earlier implementation walked
				// from the END and placed every new candidate at the FRONT — lower-dominance
				// regions hijacked the top rank; caught by the top-K determinism test.)
				// P3 — the ranking must prefer INSIDE candidates (d < 0) over outside ones
				// REGARDLESS of dominance: an outside high-dominance region (e.g. a nested
				// region the sample is just past) must not outrank an inside lower-dominance
				// one — the sample is INSIDE the lower one and its material must show (the
				// "opaque winner" test's ring geometry).  Rank key = (inside?, dominance DESC,
				// weight DESC) — the plan's "top K by (dominance, weight)" is understood as
				// WITHIN each inside/outside class.
				// P6 — a MASKED-OUT candidate (maskF == 0) ranks as OUTSIDE regardless of its
				// geometry: it cannot win (its weight is 0 — the bake pushes its stored distance
				// to +feather), and an unmasked lower-dominance region must show instead (the
				// "only apply to certain elevations inside" stretch goal).
				const dom = regionDoms[ri];
				const inside = d < 0 && maskF > 0;
				let insertAt = 0;
				while (insertAt < best.length) {
					const b = best[insertAt];
					const bDom = regionDoms[b.ri];
					const bInside = b.d < 0 && b.m > 0;
					if (inside != bInside) { if (inside) break; else insertAt++; continue; }
					if (dom > bDom || (dom == bDom && w > b.w)) break;
					insertAt++;
				}
				if (insertAt >= maxRegionSlots) continue;
				best.splice(insertAt, 0, {ri, d, w, m: maskF});
				if (best.length > maxRegionSlots) best.pop();
			}
			const i = row * n + col;
			// Slot 0: the base recipe — always present (the fallback).  The P3 shader starts its
			// blend chain from the base colour and mixes the region slots OVER it, so the base's
			// stored distance is never consumed by regionWeight — but keep it huge-positive so
			// regionWeight(base) = 0 holds under ANY feather (the "base never fades itself"
			// invariant; the +1 clamp would blend at ~50% under a feather-30 region's curve).
			indices[i] = 0;
			weights[i * SPLAT_MAX_MATERIALS + 0] = ABSENT_SLOT_DISTANCE;
			let winner = 0;
			// Write the top-K distances for ALL samples (both signs): the signed-distance
			// representation needs the OUTSIDE (positive) side too — the P3 shader feathers
			// across the boundary with `regionWeight(smoothstep(-feather, +feather, d))` and
			// linear-filtering the field needs the positive distances for the interpolation.
			// Only the WINNER (best[0]) decides the index + region payload — and only when it is
			// actually inside (d < 0): an outside sample (every candidate positive) falls back
			// to the base.
			if (best.length > 0) {
				for (let k = 0; k < best.length; k++) {
					const {ri, d, m} = best[k];
					const slot = regionSlots[ri];
					if (slot < 0) continue;
					const clampAmt = regionClampAmt[ri];
					// P6 — MASK BAKE: the stored distance is `maskedDistance(d, feather, m)` —
					// the signed distance whose regionWeight equals the region's falloff × the
					// composite mask factor (the exact inverse of the P3 smoothstep feather;
					// verified numerically).  The shader needs NO mask data: the baked field
					// feathers the mask's edge per fragment through the LINEAR filter.  Maskless
					// regions (m = 1) are byte-identical to P5.  The bake uses the region's OWN
					// feather (Set9 P2).
					const dStored = maskedDistance(d, regionFeather[ri], m);
					// Set9 P2 (§3.1/§3.2) — PER-REGION RESCALE into the slot's feather scale,
					// AFTER the mask bake and BEFORE the storage clamp (bake-then-scale composes
					// because both are monotone in `d`): a region whose authored feather is
					// narrower than the slot's has its stored distance scaled by
					// `k = slotFeather/regionFeather` so the shader's `regionWeight(d',
					// slotFeather)` reproduces the region's authored falloff `regionWeight(d,
					// regionFeather)` EXACTLY (a change of units — no halo widening).
					// `regionFeather == 0` → the sign-saturation bake.  The min-distance union
					// guard below still applies to the RESCALED value (same-recipe regions share
					// the slot; the nearer one wins), so the union remains "inside if inside ANY".
					const dScaled = rescaleRegionDistance(dStored, regionFeather[ri], slotFeather[slot], clampAmt);
					const dClamped = clampDistanceForStorage(dScaled, clampAmt);
					const prev = weights[i * SPLAT_MAX_MATERIALS + slot];
					// A slot may be carried by OTHER (same-recipe) regions evaluated later in the
					// best-list — if one of them is closer (more negative), the slot's distance
					// must be the MINIMUM (the union of same-recipe regions is "inside if inside
					// ANY", i.e. the min distance).  CRITICAL: a POSITIVE (outside) distance must
					// NEVER overwrite a NEGATIVE (inside) one — the winner's negative must win
					// (the "regions payload" test: the centre was water-inside, but the outer
					// sand slot's positive +2 got stored OVER its negative −2 → the sample
					// looked outside-sand.  The min-distance guard below handles that).
					if (k == 0 || dClamped < prev) {
						weights[i * SPLAT_MAX_MATERIALS + slot] = dClamped;
					}
					// P6 — the winner gate needs the MASK too: a region that is inside (d < 0)
					// but MASKED OUT (m == 0 — the bake pushed its distance to +feather) must
					// NOT claim the winner slot / region payload — the sample falls back to the
					// base (or the next in-class candidate's material shows per its own weight).
					if (k == 0 && best[0].d < 0 && best[0].m > 0) { indices[i] = slot; winner = (ctx.originalIndices[ri] ?? 0) + 1; }
				}
				// P3 OPAQUE — AFTER all the slot writes (a clear inside the k-loop would be
				// overwritten by the later k's write): when the winning region is OPAQUE (a hard
				// override, no blend at all, plan §3.2), every LOWER-ranked candidate with
				// STRICTLY LOWER dominance (same-dominance ties must still blend) must not blend,
				// which regionWeight would do for its positive distance.  Clear those slots'
				// distances to ABSENT_SLOT_DISTANCE → regionWeight = 0 for ANY feather (a +1
				// clamp would leak ~50% under a feather-30 underlying region) → the underlying
				// material contributes NOTHING at this sample (plan §7 GPU test 2: no trace of
				// the lower-dominance material inside an opaque region).  The winner's own
				// distance is untouched — regionWeight(−d) = 1 inside.  (An OPAQUE region that
				// does NOT win this sample must NOT silence the actual winner — the clear only
				// fires when the opaque region IS the winner.  P6: the winner gate includes the
				// mask — a masked-out region never wins, so it never fires the opaque clear.)
				// Set12 followup (2026-08-10): a same-RECIPE lower-dominance region shares the
				// winner's SLOT (assignRegionSlots collapses same-recipe regions onto one slot) —
				// clearing its slot would erase the winner's own distance (the "dominance 11 on a
				// water lake vanishes" bug).  Skip the clear when the loser's slot == the
				// winner's slot (same material — the union's min distance already governs).
				if (best[0].d < 0 && best[0].m > 0) {
					const winnerRi = best[0].ri;
					const winnerSlot = slotsByRegion[winnerRi];
					if (winnerSlot >= 0 && opaqueSlots.has(winnerSlot)) {
						for (let kk = 1; kk < best.length; kk++) {
							if (regions[best[kk].ri].dominance >= regions[winnerRi].dominance) continue; // ties still blend
							const os = slotsByRegion[best[kk].ri];
							if (os < 0 || os == winnerSlot) continue; // same-recipe loser shares the slot — never clear it
							weights[i * SPLAT_MAX_MATERIALS + os] = ABSENT_SLOT_DISTANCE;
						}
					}
				}
			}
			regionsArr[i] = winner;
			// Slot-indexed diagnostic (slot 0 = the base recipe — see the map's setup above).
			usageCounts.set(0, (usageCounts.get(0) ?? 0) + 1);
		}
	}

	// Recipe ids actually used by the chunk, in slot order (slot 0 = the base) — computed by
	// {@link assignRegionSlots} (see above); `overflowDropped` likewise.

	return {
		chunk: {indices, weights, recipeIDs, regions: regionsArr, slotDominance, slotFeather, slotSoftUnion, slotNoiseAmp, slotNoiseScale},
		overflowDropped,
		usageCounts,
	};
}

/** Broad phase (plan §3.3 step 1): filter the map's regions to those whose (feather + noiseAmp)-
 *  inflated bbox intersects the chunk's sample bounds.  Hundreds of map regions collapse to a
 *  handful per chunk — the only P2 mitigation (per-region edge-grid caching is a FUTURE
 *  mitigation; the user profiles after P2).  The chunk bounds are inflated by
 *  max(feather, edgeNoise.amplitude) per region before the test, so an under-inflated region bbox
 *  can never drop an influencing region (the bridge inflates by feather + noiseAmp too —
 *  conservative in the safe direction; MaterialRegions.test asserts the bbox inflation). */
export function filterRegionsForChunk(regions: readonly MaterialRegion[], chunkBounds: {x: number; y: number; width: number; height: number}): MaterialRegion[] {
	const out: MaterialRegion[] = [];
	for (const r of regions) {
		const inflate = Math.max(r.pathFeature.feather ?? 0, r.pathFeature.edgeNoise?.amplitude ?? 0);
		const cx = chunkBounds.x - inflate, cy = chunkBounds.y - inflate;
		const cw = chunkBounds.width + inflate * 2, ch = chunkBounds.height + inflate * 2;
		// NOTE: pass a REAL Rect — the real Intersects reads other.Left/Right (instance getters);
		// a plain object makes them undefined → always false (Set12 water bug; the Node stub masked it).
		const chunkRect = new Rect(cx, cy, cw, ch, false);
		if (r.bbox.Intersects(chunkRect)) {
			out.push(r);
		}
	}
	return out;
}

/** As {@link filterRegionsForChunk}, but ALSO returns each kept region's index in the ORIGINAL
 *  (full-map) list — the params-texture row the region payload must reference.  The rasterizer's
 *  per-chunk region list is a bbox-filtered SUBSET of the map's regions; the winning-region index
 *  emitted per sample must index the FULL map list (the renderer builds one params row per map
 *  region), not the subset. */
export function filterRegionsForChunkWithIndices(
	regions: readonly MaterialRegion[],
	chunkBounds: {x: number; y: number; width: number; height: number},
): {regions: MaterialRegion[]; originalIndices: number[]} {
	const kept: MaterialRegion[] = [];
	const indices: number[] = [];
	regions.forEach((r, i)=>{
		const inflate = Math.max(r.pathFeature.feather ?? 0, r.pathFeature.edgeNoise?.amplitude ?? 0);
		const cx = chunkBounds.x - inflate, cy = chunkBounds.y - inflate;
		const cw = chunkBounds.width + inflate * 2, ch = chunkBounds.height + inflate * 2;
		// NOTE: real Rect instance, NOT a plain object — see filterRegionsForChunk's note.
		const chunkRect = new Rect(cx, cy, cw, ch, false);
		if (r.bbox.Intersects(chunkRect)) {
			kept.push(r);
			indices.push(i);
		}
	});
	return {regions: kept, originalIndices: indices};
}

/** A chunk's CONTRIBUTING regions: the bbox-overlap broad phase ({@link filterRegionsForChunkWithIndices})
 *  MINUS the regions provably unable to affect a single fragment of the chunk.  A region whose
 *  polygon is uniformly OUTSIDE and cap-saturated over the whole chunk rect contributes exactly
 *  weight 0 at every sample, so dropping it is EXACTLY weight-preserving — never an approximation.
 *
 *  The single source of truth for "which regions get slots", used by ALL THREE call sites that
 *  derive a chunk's region/slot set — they MUST apply the identical filter, or the per-chunk
 *  program key (renderer) and the rasterised splat slot order (rasteriser) diverge and `eval_i`
 *  reads the wrong slot with no error (the Set12 `GrassInsideSnow` snow→water bug class):
 *    1. {@link rasterizeSplatFromArrays} → {@link rasterizeSplatChunk} — the splat data itself;
 *    2. `TerrainRenderer.chunkRecipeSetFor` — the per-chunk program fetch;
 *    3. {@link enumerateChunkRecipeSets} — the warm-up enumerator.
 *  The renderer/enumerator pass this helper's `regions` to {@link assignRegionSlots} (so they get
 *  the SAME slot assignment as the rasteriser), while the rasteriser uses its own `ctx.regions`
 *  (the helper's output, with `originalIndices`).
 *
 *  The narrow-phase test is {@link regionDistanceOverRect}: it returns a FINITE value iff the
 *  region's boundary is farther than `cap` from the whole chunk rect (sign exact, magnitude
 *  saturated).  `constD != null && constD > 0` ⇒ every sample's distance is `≥ cap ≥ feather` ⇒
 *  `regionWeight = 0` for every fragment — the provably non-contributing case.  `constD != null
 *  && constD < 0` (uniformly inside, full coverage) and `null` (boundary within cap — genuinely
 *  influences) both KEEP their slots.
 *
 *  The `cap` passed here (feather + noiseAmp + strokeW/2 + 1) mirrors what the rasteriser's own
 *  per-region prep computes, so a region dropped here would likewise saturate inside the
 *  rasteriser's per-sample loop (the renderer/enumerator never rasterise, so they must derive the
 *  same cap independently).
 *
 *  @param capOverride An optional cap override for callers that must test against a SPECIFIC cap
 *   (the rasteriser reuses its own per-region `regionCap`, including the +1 m slack).  When
 *   omitted, the per-region feather+noise+stroke cap is used.
 *  @returns The contributing regions + each kept region's index in the ORIGINAL (full-map) list
 *   (the params-texture row — see {@link filterRegionsForChunkWithIndices}). */
export function filterRegionsContributingToChunk(
	regions: readonly MaterialRegion[],
	chunkBounds: {x: number; y: number; width: number; height: number},
	capOverride?: number,
): {regions: MaterialRegion[]; originalIndices: number[]} {
	const broad = filterRegionsForChunkWithIndices(regions, chunkBounds);
	const kept: MaterialRegion[] = [];
	const indices: number[] = [];
	for (let i = 0; i < broad.regions.length; i++) {
		const r = broad.regions[i];
		const sw = (r.pathFeature.strokeWidth ?? 0) / 2;
		const cap = capOverride ?? (Math.max(r.pathFeature.feather ?? 0, r.pathFeature.edgeNoise?.amplitude ?? 0) + sw + 1);
		const field = buildRegionDistanceField(r.pathFeature.mode, r.polylines, r.closed);
		const constD = regionDistanceOverRect(field, chunkBounds.x, chunkBounds.y, chunkBounds.width, chunkBounds.height, cap);
		// Uniformly OUTSIDE + cap-saturated ⇒ contributes nothing to a single fragment.  Keep
		// uniformly-inside (full coverage, `constD < 0`) and genuinely-influencing (`null`).
		if (constD != null && constD > 0) continue;
		kept.push(r);
		indices.push(broad.originalIndices[i]);
	}
	return {regions: kept, originalIndices: indices};
}

/** Convenience wrapper: rasterise from flat heights/normals arrays (worker layout — heights n×n, normals n×n×3 z-up) + the chunk's world bounds. Used by the renderer AND the Node-lane tests (no GL/worker). */
export function rasterizeSplatFromArrays(
	x: number, y: number, w: number, h: number,
	heights: Float32Array, normals: Float32Array,
	n: number,
	opts: SplatRasterizeOptions,
	regions: readonly MaterialRegion[] = [],
	baseRecipeID = "dirt",
): {chunk: SplatChunk; overflowDropped: string[]; usageCounts: Map<number, number>} {
	if (heights.length != n * n) throw new SplatRasterizeError(`heights must be ${n}×${n} (got ${heights.length})`);
	if (normals.length != n * n * 3) throw new SplatRasterizeError(`normals must be ${n}×${n}×3 (got ${normals.length})`);
	const invN1 = 1 / (n - 1);
	const source: SplatSampleSource = {
		x: (_row, col)=>x + (col * invN1) * w,
		y: (row, _col)=>y + (row * invN1) * h,
		elevation: (row, col)=>heights[row * n + col],
		slope: (row, col)=>{
			const ni = (row * n + col) * 3;
			return slopeFromNormal(normals[ni], normals[ni + 1], normals[ni + 2]);
		},
	};
	const ctx: SplatRegionContext = {
		...filterRegionsContributingToChunk(regions, {x, y, width: w, height: h}),
		baseRecipeID,
		maxRegionSlots: SPLAT_TOP_K_REGIONS,
	};
	return rasterizeSplatChunk(n, source, opts, ctx);
}

export {packSplatChunk};
